import { UploadApi, UploadCancellation } from "../api/UploadApi";
export interface SavedUpload {
  requestKey: string;
  streamId: string;
  intentId?: string;
  key?: string;
  name: string;
  size: number;
  contentType: string;
  state: "active" | "cancelled" | "completed";
  releasePending?: boolean;
  releaseAfter?: string | null;
  /** The user cleared it from the tray; reload recovery still settles it but never shows it again. */
  dismissed?: boolean;
}
const prefix = "streaminal-upload:";
export function savedUploads(): SavedUpload[] {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(prefix))
      .map((k) => JSON.parse(localStorage.getItem(k)!));
  } catch {
    return [];
  }
}
export function rememberUpload(record: SavedUpload) {
  try {
    const key = prefix + record.streamId + ":" + record.requestKey;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        if (JSON.parse(stored)?.dismissed) record.dismissed = true;
      } catch {
        /* Replace a malformed receipt with the current authoritative record. */
      }
    }
    localStorage.setItem(key, JSON.stringify(record));
  } catch {
    /* Storage may be disabled; server receipt remains authoritative. */
  }
}
/** Receipts are only kept while the server may still hold quota for them (UC-5.3). */
export function forgetUpload(streamId: string, requestKey: string) {
  try {
    localStorage.removeItem(prefix + streamId + ":" + requestKey);
  } catch {
    /* Storage may be disabled. */
  }
}

/** The user cleared these from the tray: keep each receipt for server cleanup, never show it again. */
export function dismissUploads(requestKeys: string[]) {
  for (const record of savedUploads())
    if (requestKeys.includes(record.requestKey))
      rememberUpload({ ...record, dismissed: true });
}
const live = new Map<
  string,
  { lifetime: UploadLifetime; controller: AbortController }
>();
const liveKey = (streamId: string, requestKey: string) =>
  `${streamId}:${requestKey}`;
export function liveUpload(streamId: string, requestKey: string) {
  return live.get(liveKey(streamId, requestKey));
}
/** Associate the actual transfer until its owner tears down, including save response races. */
export function registerLiveUpload(
  lifetime: UploadLifetime,
  controller: AbortController
) {
  const key = liveKey(lifetime.record.streamId, lifetime.record.requestKey);
  const entry = { lifetime, controller };
  live.set(key, entry);
  return () => {
    if (live.get(key) === entry) live.delete(key);
  };
}
/** One browser transfer lease. The server remains responsible after reload or lost network. */
export class UploadLifetime {
  record: SavedUpload;
  private timer?: ReturnType<typeof setInterval>;
  private renewing = false;
  private cancellation?: Promise<UploadCancellation | undefined>;
  onExpired: (error: unknown) => void = () => {};
  onCancellation: (result: UploadCancellation) => void = () => {};
  constructor(record: SavedUpload) {
    this.record =
      savedUploads().find(
        (r) =>
          r.streamId === record.streamId && r.requestKey === record.requestKey
      ) ?? record;
    rememberUpload(this.record);
  }
  bind(intentId: string, key: string) {
    this.record = { ...this.record, intentId, key };
    rememberUpload(this.record);
    if (this.record.state === "cancelled") {
      this.cancellation = undefined;
      void this.cancel();
      return;
    }
    this.stop();
    this.timer = setInterval(() => {
      void this.heartbeat();
    }, 300000);
  }
  async heartbeat() {
    if (
      this.renewing ||
      !this.record.intentId ||
      this.record.state !== "active"
    )
      return;
    this.renewing = true;
    try {
      await UploadApi.heartbeat(this.record.streamId, this.record.intentId);
    } catch (error: any) {
      if (error?.response?.data?.code === "UPLOAD_INTENT_EXPIRED") {
        this.stop();
        this.onExpired(error);
      }
      // Transient response loss retries at the next heartbeat; finalize still checks the lease.
    } finally {
      this.renewing = false;
    }
  }
  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }
  finish() {
    this.stop();
    this.record.state = "completed";
    this.record.releasePending = false;
    this.record.releaseAfter = null;
    rememberUpload(this.record);
  }
  cancel(): Promise<UploadCancellation | undefined> {
    this.stop();
    if (this.record.state === "completed") return Promise.resolve(undefined);
    this.record.state = "cancelled";
    this.record.releasePending = true;
    rememberUpload(this.record);
    if (!this.record.intentId) return Promise.resolve(undefined);
    if (this.cancellation) return this.cancellation;
    this.cancellation = UploadApi.cancel(
      this.record.streamId,
      this.record.intentId
    )
      .then(({ data }) => {
        if (this.record.state === "completed") return undefined;
        Object.assign(this.record, data);
        rememberUpload(this.record);
        this.onCancellation(data);
        return data;
      })
      .catch(() => {
        if (this.record.state === "completed") return undefined;
        // A lost cancellation response is not quota release. Keep exact identity for reload/retry.
        this.cancellation = undefined;
        const result: UploadCancellation = {
          state: "cancelled",
          releasePending: true,
          releaseAfter: null,
        };
        this.onCancellation(result);
        return result;
      });
    return this.cancellation;
  }
}
